import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useCreateMarketingCampaign } from '../../queries/marketingCampaigns';
import { MarketingOffer } from '../../queries/marketingOffers';
import { MarketingSegment } from '../../queries/marketingSegments';

interface CampaignFormDialogProps {
  offers: MarketingOffer[];
  segments: MarketingSegment[];
  onClose: () => void;
}

export function CampaignFormDialog({ offers, segments, onClose }: CampaignFormDialogProps) {
  const [name, setName] = useState('');
  const [offerId, setOfferId] = useState<number | ''>('');
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateMarketingCampaign();

  const activeOffers = offers.filter((o) => o.is_active);
  const canSave = name.trim() && offerId !== '';

  const handleSave = () => {
    if (offerId === '') return;
    setError(null);
    create.mutate(
      { name, offer_id: offerId, segment_id: segmentId === '' ? undefined : segmentId, expires_at: expiresAt || undefined },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Save failed.')) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Create campaign</Dialog.Title>

          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="campaign-name" className="mb-1 block text-sm font-medium">Name</label>
              <input
                id="campaign-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="campaign-offer" className="mb-1 block text-sm font-medium">Offer</label>
              <select
                id="campaign-offer"
                value={offerId}
                onChange={(e) => setOfferId(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">Select an offer…</option>
                {activeOffers.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="campaign-segment" className="mb-1 block text-sm font-medium">Segment (optional)</label>
              <select
                id="campaign-segment"
                value={segmentId}
                onChange={(e) => setSegmentId(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">No segment</option>
                {segments.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} (~{s.estimated_count ?? '?'})</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="campaign-expiry" className="mb-1 block text-sm font-medium">Code expiry date (optional)</label>
              <input
                id="campaign-expiry"
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
            </div>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={create.isPending || !canSave}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
